import { withDevice } from '@/utils/sender-device'
import axios from "axios";
import {static_store, store} from "@/store";
import { markReadingViewed } from "@/utils/analytics";

// NOTE: the reading-conversation refs (readingTitle, StaticSuggestions, …)
// are resolved with a LAZY dynamic import at call-time, NOT a static import.
// useReaderConversation statically imports this file (load_card_reading_session),
// so a static `import … from '@/utils/reader-conversation'` here would form a
// circular dependency. Under Vite HMR that cycle could re-instantiate the
// composable's shared refs, leaving the component bound to a different ref
// than this file writes to (post-reading buttons silently empty). The dynamic
// import has no static edge, so the cycle — and the HMR fragility — is gone.
let _rcPromise = null;
function readerConversationRefs() {
  if (!_rcPromise) _rcPromise = import("@/utils/reader-conversation");
  return _rcPromise;
}

// This should be passed in from component using it (e.g., `selectedCards`)
let selectedCardsRef = null;

/**
 * Populate the reading panel directly from the load-reading-session HTTP
 * response so loading a COMPLETED reading never depends on the socket
 * stream (which only drives the chat replay). Flips readingIsReady so the
 * panel renders immediately — no "Weaving…" state, no race.
 */
function populateReadingContent(rc, content) {
  if (!content) return;
  // Legacy per-position refs the panel reads for 1/3/4-card spreads.
  const positionRefs = {
    readingPast: rc.readingPast, readingPresent: rc.readingPresent,
    readingFuture: rc.readingFuture, readingAdvice: rc.readingAdvice,
  };
  rc.readingTitle.value = content.readingTitle || "";
  rc.readingAnalysis.value = content.readingAnalysis || "";
  rc.ReadingSummaryTitle.value = content.ReadingSummaryTitle || "";
  rc.readingIllustrationTitle.value = content.readingIllustrationTitle || "";
  rc.readingImage.value = content.readingImage || "";
  // Her card's short phrase on a gift's own version (`positions[].line`,
  // 2026-10-19, R4.40), by position. Carried for the room; the room's layout
  // does not show it yet (Chris has not decided the room's design).
  const lines = {};
  (content.positions || []).forEach((p) => { if (p && p.reference && p.line) lines[p.reference] = p.line; });
  store.reading_position_lines = lines;
  (content.positions || []).forEach((p) => {
    if (!p || !p.reference) return;
    // Spread-agnostic position map (used by Celtic + future spreads)…
    rc.readingPositions.value[p.reference] = p.message || "";
    // …plus the legacy refs the current panel reads for 1/3/4 cards.
    if (positionRefs[p.reference]) positionRefs[p.reference].value = p.message || "";
  });
  rc.readingIsReady.value = true;
}

export function setSelectedCardsRef(ref) {
  selectedCardsRef = ref;
}


export async function load_astrology_reading_session(reading_session_id, session_id){
    const address = `${static_store.apiUrl}/horoscope/load-astrology-reading-session/`

    try {
        const response = await axios.post(address, {
          reading_session_id: reading_session_id,
          session_id: session_id
        });
        const { uuid, is_shared, is_paid, is_subscribed } = response.data;

        localStorage.setItem('querent_id', response.data.querent_id);
        store.latest_reading_session = uuid;
        store.reading_session_id = uuid
        store.is_latest_reading_session_shared = is_shared;
        store.latest_reading_session_is_paid = is_paid;
        store.querent_subscribed = is_subscribed;
        store.astrology_ready = response.data.astrology_ready
        return "PERSONALIZED_ASTROLOGY"

      } catch (error) {
        if (error.response && error.response.status === 404) {
          console.error('Reading not found.');
          alert('Reading not found.');
        } else {
          console.error('An error occurred:', error);
          alert('An unexpected error occurred. Please try again later.');
        }
      }
}


export async function load_reading_session(reading_session_id, session_id, inv = '') {
  // Opening an existing reading is viewing it, not doing one (analytics).
  markReadingViewed(reading_session_id);
  try {
    const response = await axios.post(static_store.apiUrl + '/reader/load-reading-session/', {
      session_id: session_id,
      reading_session_id: reading_session_id,
      // A link someone was sent (?inv=) opens the reading as it is (SP-L3).
      ...(inv ? { inv } : {}),
    });

    // The owner made this reading private after the link was shared — the link
    // no longer resolves. Let the caller surface the message.
    if (response.data && response.data.private) {
      // A "Send a link" someone else opened first (it opens for one person,
      // 2026-10-20): the page says so kindly, not "made private".
      if (response.data.taken) return { private: true, taken: true, message: response.data.message || 'This one was opened already.' };
      return { private: true, message: response.data.message || 'This reading has been made private.' };
    }
    // Someone outside a public reading gets its public version, and nothing
    // else of it (2026-10-19, SP-S1): the page shows it as a letter.
    if (response.data && response.data.public_version) {
      return { publicVersion: response.data.public_version };
    }

    store.current_reader_subdomain = response.data.reader_subdomain;

    if (response.data.reading_type === "PERSONALIZED_ASTROLOGY"){
      return load_astrology_reading_session(reading_session_id, session_id)
    }

    return await applyLoadedReading(response);
  } catch (error) {
    console.error('Error loading reading session:', error);
    throw error;
  }
}

/**
 * Care Package: the package opens in the Mystic's room. The sender's reading
 * is loaded through the invite (read-only on the server) and applied exactly
 * as a Journal open is. The owner's Rate / Share / Finish bar is not hers;
 * the gift's own chips replace it (utils/care-room.js).
 * Returns { card_count, gift, gift_room } | { revoked } | { notYours }.
 */
export async function open_care_package(inv, session_id, { quiet = false } = {}) {
  let response
  try {
    // quiet: put the panel back without replaying the chat (after a deeper draw).
    response = await axios.post(static_store.apiUrl + '/reader/care-package/open/',
      { inv, session_id, ...withDevice(), ...(quiet ? { quiet: true } : {}) })
  } catch (e) {
    const status = e && e.response && e.response.status
    if (status === 410) return { revoked: true }
    if (status === 403) return { notYours: true }
    throw e
  }
  store.current_reader_subdomain = response.data.reader_subdomain
  const loaded = await applyLoadedReading(response)
  const rc = await readerConversationRefs()
  rc.StaticSuggestions.value = []
  // introduction: the server's word on whether her introduction lasts (R4.21).
  return { ...(loaded || {}), gift: response.data.gift || {}, gift_room: response.data.gift_room || {},
           // The room's words (2026-10-22): { first_message, chips, respond, pull_label, … },
           // and the letter's data (the reading-preview shape, her version).
           room_text: response.data.room || null, letter: response.data.letter || null,
           introduction: !!response.data.introduction,
           // The end of her introduction is owed (R8.3): the room asks for its line.
           introduction_moment: response.data.introduction_moment || null,
           // The staged bar and its stage (navigation API §2).
           nav: response.data.nav || null }
}

// One applier for a loaded reading: the Journal's load and the care
// package's open fill the panel the same way.
async function applyLoadedReading(response) {
  if (response && response.data && Array.isArray(response.data.cards)) {
    load_card_reading_session(response.data.cards);
    const { uuid, is_shared, is_paid, is_subscribed } = response.data;
    store.latest_reading_session = uuid;
    store.is_latest_reading_session_shared = is_shared;
    store.latest_reading_session_is_paid = is_paid;
    // Only the server's word opens a link-held reading to a signed-out viewer.
    store.link_holder = response.data.access === 'invite';
    // The share sheet's "Your reading · Mystic AIda · Oct 12".
    store.latest_reading_date = response.data.reading_date || '';
    // Talk it through: where its conversation stands (the owner's only).
    store.talk = response.data.talk || null;
    // The cards pulled while talking it through (2026-10-22): the table row's tiles.
    store.talk_cards = Array.isArray(response.data.talk_cards) ? response.data.talk_cards : [];
    // Her own rating of it (the owner's only): the end-of-reading crystals show it.
    if (uuid && response.data.my_rating) {
      store.reading_ratings = { ...(store.reading_ratings || {}), [uuid]: Number(response.data.my_rating) };
    }
    // Care Package: who this reading is for (null when it is for the seeker).
    store.reading_gift = (response.data.gift && response.data.gift.is_gift) ? response.data.gift : null;
    store.querent_subscribed = is_subscribed;
    // Render the panel straight from the HTTP response and mark it ready
    // — loading a completed reading is a pure fetch, no socket needed.
    // Refs resolved lazily (see readerConversationRefs) to avoid the
    // circular-import HMR fragility.
    const rc = await readerConversationRefs();
    populateReadingContent(rc, response.data.reading_content);
    // Post-reading menu (Rate / Share / Finish) from the same response, so
    // the chat-window buttons render without depending on the socket replay
    // arriving. Clear any stale "Conjuring insight…".
    if (Array.isArray(response.data.post_reading_actions)) {
      rc.StaticSuggestions.value = response.data.post_reading_actions;
    }
    rc.loadingHeadsUpMessage.value = '';
    // Derive reading_type from the actual card count served by the
    // BE so the right game component (OneCardReading vs
    // FourCardReading) mounts on resume. Spread.card_count would be
    // the authoritative source if the BE serves it; until then,
    // cards.length is a clean proxy for the loaded shape.
    store.reading_type = response.data.cards.length === 1 ? 'one' : 'four';
    // Hand the real count back. store.reading_type collapses everything that
    // is not a single card into 'four', which is fine for choosing between
    // the two dedicated game components but useless to a caller that has to
    // mount a THREE-card spread. The count is right here; throwing it away
    // is what left the Duality Draw being mounted by the four-card handler.
    return { card_count: response.data.cards.length };
  } else {
    console.error('Invalid response format or missing cards:', response.data);
  }
}

/**
 * Push every card in `cards_to_load` into the shared selectedCardsRef.
 * Variant-blind: works for 1, 3, or 4 cards. The function is named
 * `load_card_reading_session` going forward; `load_4_card_reading_session`
 * is kept as a shim for any older caller.
 */
export function load_card_reading_session(cards_to_load) {
  if (!Array.isArray(cards_to_load)) {
    console.error('cards_to_load is not an array:', cards_to_load);
    return;
  }

  if (!selectedCardsRef || !selectedCardsRef.value) {
    console.error('selectedCardsRef is not set');
    return;
  }

  // Replace, don't append — loading a reading should show exactly its cards.
  // Without this, viewing a second reading appended its cards to the first
  // (a 4-card reading rendered 8 positions: Past…Advice + Card 5…8).
  selectedCardsRef.value.splice(0, selectedCardsRef.value.length);

  cards_to_load.forEach(card => {
    const the_card = {
      card_id: card.card_id,
      name: card.name,
      reversed: card.reversed,
      description: card.description,
      reverse_description: card.reverse_description,
      image_path: card.image_path,
    };
    selectedCardsRef.value.push(the_card);
  });
}

// Legacy alias — the previous name asserted 4-card-ness in its
// signature, but the body was always N-blind. Kept so any external
// caller (or older test) still resolves; net new code should use
// load_card_reading_session.
export const load_4_card_reading_session = load_card_reading_session;


/**
 * Scope the Studio "Card Selection" step list to the active spread.
 *
 * The reader accumulates one ``card_select_<role>`` step (and a
 * ``card_select_<role>_new_querent`` variant) for every spread they've
 * ever used — so a reader who switched 4-card → 1-card has both the
 * four_card roles (past/present/future/advice) AND the one_card role
 * (focus) sitting in the same skill-level instruction set. The Card
 * Selection tab must show ONLY the steps the ACTIVE spread needs.
 *
 * Role-scoped steps (``card_select_<role>`` / ``..._new_querent``) are
 * kept only when ``<role>`` is one of the active spread's position
 * roles. Any step that isn't role-scoped (e.g. ``card_selected_detail``,
 * a generic per-pick step) is always kept. When ``roles`` is missing or
 * empty (no spread info yet) the list is returned unchanged — a safe
 * no-op so the tab never goes blank on partial data.
 *
 * @param {Array<{step_name?: string}>} steps
 * @param {Array<string>|null|undefined} roles active spread's position roles
 * @returns {Array} the steps the active spread actually uses
 */
export function cardSelectionStepsForRoles(steps, roles) {
  if (!roles || !roles.length) return steps || [];
  const allowed = new Set(roles);
  return (steps || []).filter((s) => {
    const name = (s && s.step_name) || '';
    if (!name.startsWith('card_select_')) return true;
    const role = name.slice('card_select_'.length).replace(/_new_querent$/, '');
    if (!role) return true;
    return allowed.has(role);
  });
}